import type { ActionExecutionResult } from "../hooks/useActionExecutor"
import type {
  CoviActionConfirmationVia,
  useCoviAnalytics,
} from "../hooks/useCoviAnalytics"
import type { CoviActionItemPatch, SofiaProposedAction } from "../model/types"

const DEFAULT_EXECUTION_ERROR = "The action failed unexpectedly."

/**
 * Copy for a confirmation whose signed token has lapsed. Retrying resends the
 * same stale token and fails deterministically, so the only way forward is a
 * fresh proposal — and asking the user to type a phrase Sofia dictated left
 * them with no in-panel path at all, which is how one expired card looped for
 * turns. The card offers "Propose again" instead.
 */
export const EXPIRED_CONFIRMATION_MESSAGE =
  "This confirmation expired. Propose it again to get a fresh card."
const ANALYTICS_API_EXECUTION_ERROR = "API action failed."
const ANALYTICS_SERVER_EXECUTION_ERROR = "Server action failed."

/** Metric receipts retain fresh readers for their optional destination link. */
export function refreshesMetricReceipt(
  kind: SofiaProposedAction["kind"],
): boolean {
  return (
    kind === "create_metric" ||
    kind === "publish_python_metric" ||
    kind === "save_python_analysis_metric"
  )
}

type ActionExecutionAnalytics = Pick<
  ReturnType<typeof useCoviAnalytics>,
  "trackActionConfirmed" | "trackActionExecuted" | "trackActionFailed"
>

function analyticsFailureError(
  kind: SofiaProposedAction["kind"],
  serverExecuted: boolean,
  failureError: string,
): string {
  if (kind === "navigate") {
    return failureError
  }
  return serverExecuted
    ? ANALYTICS_SERVER_EXECUTION_ERROR
    : ANALYTICS_API_EXECUTION_ERROR
}

/**
 * Run one already-confirmed Sofia action and persist its receipt.
 *
 * Both the confirmation card and the composer’s typed-confirmation fallback
 * call this function, so “Confirm” and “yes, I confirm” share the same
 * executor, status transitions, failure handling, and analytics.
 */
export async function runConfirmedAction({
  proposal,
  via,
  serverExecuted,
  execute,
  update,
  analytics,
}: {
  proposal: SofiaProposedAction
  via: CoviActionConfirmationVia
  serverExecuted: boolean
  execute: (proposal: SofiaProposedAction) => Promise<ActionExecutionResult>
  update: (patch: CoviActionItemPatch) => void
  analytics: ActionExecutionAnalytics
}): Promise<void> {
  analytics.trackActionConfirmed({ kind: proposal.kind, via, serverExecuted })
  update({
    status: "executing",
    error: undefined,
    feedback: undefined,
    href: undefined,
    expired: undefined,
  })
  try {
    const result = await execute(proposal)
    if (result.ok) {
      analytics.trackActionExecuted({
        kind: proposal.kind,
        via,
        serverExecuted,
      })
      update({
        status: "done",
        detail: result.detail,
        feedback: result.feedback,
        href: result.href,
        settledAt: Date.now(),
      })
      return
    }

    const failureError = result.error ?? DEFAULT_EXECUTION_ERROR
    analytics.trackActionFailed({
      kind: proposal.kind,
      via,
      serverExecuted,
      reason: "execution_error",
      error: analyticsFailureError(proposal.kind, serverExecuted, failureError),
    })
    update({
      status: "error",
      error: failureError,
      feedback: result.feedback,
      href: result.href,
      expired: result.expired,
    })
  } catch (error: unknown) {
    const failureError =
      error instanceof Error && error.message
        ? error.message
        : DEFAULT_EXECUTION_ERROR
    analytics.trackActionFailed({
      kind: proposal.kind,
      via,
      serverExecuted,
      reason: "exception",
      error: analyticsFailureError(proposal.kind, serverExecuted, failureError),
    })
    update({
      status: "error",
      error: failureError,
      href: undefined,
    })
  }
}
